import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./Results.css";
import { apiRequest, logout, getTaskStatus } from "../utils/api";

export default function Results() {
  const [backendData, setBackendData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState("Queuing training job…");
  const [error, setError] = useState(null);
  const [targetVariable, setTargetVariable] = useState("");
  const [time, setTime] = useState("");
  const pollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchResults();
    const interval = setInterval(() => {
      const currentTime = new Date();
      const hours = currentTime.getHours() % 12 || 12;
      const minutes = currentTime.getMinutes().toString().padStart(2, "0");
      const seconds = currentTime.getSeconds().toString().padStart(2, "0");
      const ampm = currentTime.getHours() >= 12 ? "PM" : "AM";
      setTime(`${hours}:${minutes}:${seconds} ${ampm}`);
    }, 1000);
    return () => {
      clearInterval(interval);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  const fetchResults = async () => {
    setLoading(true);
    setLoadingMessage("Queuing training job…");
    try {
      const selectedTarget = localStorage.getItem("selectedTarget");
      setTargetVariable(selectedTarget);

      const selectedAlgorithms = JSON.parse(localStorage.getItem("selectedAlgorithms") || "[]");
      const splitConfig = JSON.parse(localStorage.getItem("dataSplitConfig") || '{"use_100_percent": true}');

      const requestBody = {
        selected_algorithms: selectedAlgorithms,
        target_variable: selectedTarget,
        use_100_percent: splitConfig.use_100_percent !== false,
      };
      if (!requestBody.use_100_percent && splitConfig.train_ratio && splitConfig.test_ratio) {
        requestBody.train_ratio = splitConfig.train_ratio;
        requestBody.test_ratio = splitConfig.test_ratio;
      }

      // Dispatch job — receives 202 with { task_id, job_id } immediately
      const dispatch = await apiRequest("/api/algorithms/run", {
        method: "POST",
        body: requestBody,
      });

      const { task_id: taskId, job_id: jobId } = dispatch;

      // Persist job_id for the predict page
      localStorage.setItem("trainingJobId", String(jobId));

      setLoadingMessage("Training in progress… this may take a few minutes.");

      // Poll every 4 s until completed or failed
      pollRef.current = setInterval(async () => {
        try {
          const statusRes = await getTaskStatus(taskId);
          if (statusRes.status === "completed") {
            clearInterval(pollRef.current);
            const payload = statusRes.result;
            if (!payload || payload.status === "error") {
              setError(payload?.message || "Training failed.");
            } else {
              setBackendData(payload);
            }
            setLoading(false);
          } else if (statusRes.status === "failed") {
            clearInterval(pollRef.current);
            setError(statusRes.error_message || "Training job failed.");
            setLoading(false);
          }
          // "pending" / "running" — keep polling
        } catch (pollErr) {
          console.error("Polling error:", pollErr);
        }
      }, 4000);

    } catch (e) {
      console.error(e);
      setError("This dataset is not available for this analysis.");
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="results-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{loadingMessage}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="results-page">
        <div className="error-container">
          <h2>Error Loading Results</h2>
          <p>{error}</p>
          <button onClick={() => navigate("/algorithm-selection")} className="primary-button">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const resultsArray = Array.isArray(backendData?.results)
    ? backendData.results
    : [];

  // Determine if results are classification or regression based on available metrics
  const isClassification = resultsArray.length > 0 && 
    resultsArray.some(r => !r.error && (r.precision !== undefined || r["recall (sensitivity)"] !== undefined));
  const isRegression = resultsArray.length > 0 && 
    resultsArray.some(r => !r.error && (r.r2_score !== undefined || r.mae !== undefined));

  // Determine model type for an algorithm
  const getModelType = (algorithmName) => {
    const regressionAlgorithms = [
      "Linear Regression",
      "Ridge Regression",
      "Lasso Regression",
    ];
    
    const classificationAlgorithms = [
      "Logistic Regression",
      "Naive Bayes",
    ];
    
    if (regressionAlgorithms.includes(algorithmName)) {
      return "regression";
    }
    
    if (classificationAlgorithms.includes(algorithmName)) {
      return "classification";
    }
    
    // Determine based on available metrics
    const result = resultsArray.find(r => r.algorithm === algorithmName);
    if (result && !result.error) {
      if (result.r2_score !== undefined) {
        return "regression";
      }
      if (result.precision !== undefined) {
        return "classification";
      }
    }
    
    return "both";
  };

  if (resultsArray.length === 0 || resultsArray.every((r) => r.error)) {
    return (
      <div className="results-page">
        <div className="error-container">
          <h2>No Results Available</h2>
          <p>Chosen Target Variable is not suitable for this analysis. Choose another Target Variable.</p>
          <button onClick={() => navigate("/supervised")} className="primary-button">
            Choose Different Target Variable
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="results-page">
      {/* Top Navigation Bar */}
      <div className="top-nav">
        <div className="nav-content">
          <div className="nav-left-group">
            <div className="nav-logo" onClick={() => navigate("/")}>
              <img src="/HDASLogo.png" alt="HDAS" style={{ height: "80px", width: "auto" }} />
            </div>
            <button className="back-nav-button" onClick={() => navigate("/algorithm-selection")}>
              Back
            </button>
          </div>
          <div className="nav-right">
            <div className="digital-clock">{time}</div>
            <button onClick={handleLogout} className="logout-button">Logout</button>
          </div>
        </div>
      </div>

      {/* Header Section */}
      <div className="page-header">
        <div className="header-content">
          <h1>Algorithm Results</h1>
          <p className="header-subtitle">
            Target Variable: <strong>{targetVariable}</strong>
            {isClassification && !isRegression && (
              <span className="task-type-badge classification">Classification Task</span>
            )}
            {isRegression && !isClassification && (
              <span className="task-type-badge regression">Regression Task</span>
            )}
            {isClassification && isRegression && (
              <span className="task-type-badge both">Mixed Task</span>
            )}
          </p>
        </div>
      </div>

      {/* Main Content - Table Only */}
      <div className="page-content">
        <div className="table-wrapper">
          <table className="results-table">
            <thead>
              <tr>
                <th>Algorithm</th>
                {isClassification && !isRegression ? (
                  <>
                    <th>Accuracy (%)</th>
                    <th>Precision (%)</th>
                    <th>Sensitivity (%)</th>
                    <th>F1-Score (%)</th>
                    <th>Specificity (%)</th>
                    <th>Fold Accuracies</th>
                  </>
                ) : isRegression && !isClassification ? (
                  <>
                    <th>R² Score</th>
                    <th>MAE</th>
                    <th>RMSE</th>
                    <th>Fold R² Scores</th>
                  </>
                ) : (
                  <>
                    <th>Type</th>
                    <th>Score</th>
                    <th>Additional Metrics</th>
                    <th>Fold Scores</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {resultsArray.map((r, i) => {
                if (r.error) {
                  const colSpan = isClassification && !isRegression ? 7 : 
                                 isRegression && !isClassification ? 5 : 5;
                  return (
                    <tr key={i} className="error-row">
                      <td colSpan={colSpan}>
                        {r.algorithm}: {r.error}
                      </td>
                    </tr>
                  );
                }

                const modelType = getModelType(r.algorithm);
                const isRegResult = r.r2_score !== undefined;

                return (
                  <tr key={i}>
                    <td className="algorithm-name">
                      <div className="algorithm-name-cell">
                        <span>{r.algorithm}</span>
                        <span className={`model-type-badge ${modelType}`}>
                          {modelType === "classification" && "Classification"}
                          {modelType === "regression" && "Regression"}
                          {modelType === "both" && (isRegResult ? "Regression" : "Classification")}
                        </span>
                      </div>
                    </td>
                    {isClassification && !isRegression ? (
                      <>
                        <td>{(r.accuracy * 100).toFixed(2)}</td>
                        <td>{(r.precision * 100).toFixed(2)}</td>
                        <td>{(r["recall (sensitivity)"] * 100).toFixed(2)}</td>
                        <td>{(r.f1_score * 100).toFixed(2)}</td>
                        <td>{(r.specificity * 100).toFixed(2)}</td>
                        <td>
                          <div className="fold-accuracies">
                            {r.fold_accuracies && r.fold_accuracies.map((acc, fi) => (
                              <span key={fi} className="fold-badge">
                                Fold {fi + 1}: {(acc * 100).toFixed(2)}%
                              </span>
                            ))}
                          </div>
                        </td>
                      </>
                    ) : isRegression && !isClassification ? (
                      <>
                        <td>{r.r2_score !== undefined ? r.r2_score.toFixed(4) : 'N/A'}</td>
                        <td>{r.mae !== undefined ? r.mae.toFixed(4) : 'N/A'}</td>
                        <td>{r.rmse !== undefined ? r.rmse.toFixed(4) : 'N/A'}</td>
                        <td>
                          <div className="fold-accuracies">
                            {r.fold_accuracies && r.fold_accuracies.map((r2, fi) => (
                              <span key={fi} className="fold-badge">
                                Fold {fi + 1}: {r2.toFixed(4)}
                              </span>
                            ))}
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>
                          <span className={`model-type-badge ${isRegResult ? 'regression' : 'classification'}`}>
                            {isRegResult ? 'Regression' : 'Classification'}
                          </span>
                        </td>
                        <td>
                          {isRegResult 
                            ? (r.r2_score !== undefined ? `R²: ${r.r2_score.toFixed(4)}` : 'N/A')
                            : (r.accuracy !== undefined ? `Acc: ${(r.accuracy * 100).toFixed(2)}%` : 'N/A')
                          }
                        </td>
                        <td>
                          {isRegResult 
                            ? `MAE: ${r.mae?.toFixed(4) || 'N/A'}, RMSE: ${r.rmse?.toFixed(4) || 'N/A'}`
                            : `Prec: ${r.precision ? (r.precision * 100).toFixed(2) : 'N/A'}%, Rec: ${r["recall (sensitivity)"] ? (r["recall (sensitivity)"] * 100).toFixed(2) : 'N/A'}%`
                          }
                        </td>
                        <td>
                          <div className="fold-accuracies">
                            {r.fold_accuracies && r.fold_accuracies.map((score, fi) => (
                              <span key={fi} className="fold-badge">
                                Fold {fi + 1}: {isRegResult ? score.toFixed(4) : (score * 100).toFixed(2) + '%'}
                              </span>
                            ))}
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Action Buttons */}
        <div className="action-section">
          <button onClick={() => navigate("/predict")} className="primary-button">
            Continue to Predictive Analysis
          </button>
        </div>
      </div>
    </div>
  );
}
